import React, { useState, useEffect } from 'react';
import { reportsAPI } from '../../services/garageApi';
import { formatCurrency } from '../../utils/helpers';
import BackButton from '../common/BackButton';

const CustomerBalanceReport = () => {
    const [balances, setBalances] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchCustomerBalance = async () => {
            try {
                const response = await reportsAPI.customerBalance();
                setBalances(Array.isArray(response.data) ? response.data : []);
            } catch (error) {
                console.error('Error fetching customer balances:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchCustomerBalance();
    }, []);

    const totals = balances.reduce((acc, row) => ({
        invoiced: acc.invoiced + parseFloat(row.total_invoiced || 0),
        paid: acc.paid + parseFloat(row.total_paid || 0),
        outstanding: acc.outstanding + parseFloat(row.outstanding || 0),
    }), { invoiced: 0, paid: 0, outstanding: 0 });

    if (loading) {
        return <div className="flex justify-center items-center h-64">Loading report...</div>;
    }

    return (
        <div className="space-y-6">
            {/* Report Header */}
            <div className="bg-white shadow rounded-lg p-6">
                <div className="flex justify-between items-center">
                    <div className="flex items-center gap-3">
                        <BackButton />
                        <h2 className="text-2xl font-bold text-gray-900">Customer Balance Report</h2>
                    </div>
                    <div className="text-sm text-gray-500">
                        Generated: {new Date().toLocaleDateString()}
                    </div>
                </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white rounded-lg shadow p-6">
                    <p className="text-sm font-medium text-gray-600">Total Invoiced</p>
                    <p className="text-3xl font-bold text-gray-900">{formatCurrency(totals.invoiced)}</p>
                </div>
                <div className="bg-white rounded-lg shadow p-6">
                    <p className="text-sm font-medium text-gray-600">Total Paid</p>
                    <p className="text-3xl font-bold text-green-600">{formatCurrency(totals.paid)}</p>
                </div>
                <div className="bg-white rounded-lg shadow p-6">
                    <p className="text-sm font-medium text-gray-600">Outstanding</p>
                    <p className="text-3xl font-bold text-red-600">{formatCurrency(totals.outstanding)}</p>
                </div>
            </div>

            {/* Balance Table */}
            <div className="bg-white shadow rounded-lg p-6">
                <h3 className="text-lg font-semibold mb-4">Customer Breakdown</h3>
                {balances.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">No customer balance data available.</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Invoiced</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Paid</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Outstanding</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                            </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                            {balances.map((row, index) => (
                                <tr key={index}>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                                        {row.customer?.name || row.customer?.company_name || `Customer #${row.customer_id}`}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm">{formatCurrency(row.total_invoiced)}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">{formatCurrency(row.total_paid)}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-red-600">
                                        {formatCurrency(row.outstanding)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        {parseFloat(row.outstanding) <= 0 ? (
                                            <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                                                Settled
                                            </span>
                                        ) : (
                                            <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full">
                                                Outstanding
                                            </span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Print Button */}
            <div className="flex justify-end">
                <button
                    onClick={() => window.print()}
                    className="px-6 py-2 bg-gray-800 text-white rounded-md hover:bg-gray-900"
                >
                    Print Report
                </button>
            </div>
        </div>
    );
};

export default CustomerBalanceReport;
